import { useParams, useNavigate } from 'react-router-dom';
import { useOrdemProducao, useIniciarProducao, useFinalizarProducao } from '@/hooks/useProducao';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { ArrowLeft, Play, Square, Camera } from 'lucide-react';
import { formatDate, formatDuration } from '@/lib/utils';
import { useState } from 'react';


const statusConfig: Record<string, { label: string; variant: any }> = {
  NOVO: { label: 'Novo', variant: 'secondary' },
  PAGO_CONFIRMADO: { label: 'Confirmado', variant: 'info' },
  PRODUCAO: { label: 'Em Produção', variant: 'warning' },
  AGUARDANDO_SEPARACAO: { label: 'Pronto', variant: 'success' },
  PRONTO_EXPEDICAO: { label: 'Pronto Expedição', variant: 'success' },
  ENVIADO: { label: 'Enviado', variant: 'info' },
  ENTREGUE: { label: 'Entregue', variant: 'success' },
  CANCELADO: { label: 'Cancelado', variant: 'error' },
};

export function OrdemProducao() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: ordem, isLoading } = useOrdemProducao(id!);
  const iniciarProducao = useIniciarProducao();
  const finalizarProducao = useFinalizarProducao();
  const [progresso, setProgresso] = useState(0);

  const handleIniciar = async () => {
    await iniciarProducao.mutateAsync({ id: id! });
  };

  const handleFinalizar = async () => {
    await finalizarProducao.mutateAsync({
      id: id!,
      data: {
        quantidadeBoa: ordem?.quantidadeSolicitada || 0,
        quantidadeRefugada: 0,
        tempoRealMinutos: ordem?.tempoRealMinutos || 0,
        progressoPercentual: 100,
      },
    });
  };

  if (isLoading) {
    return <div className="text-gray-900">Carregando...</div>;
  }

  if (!ordem) {
    return <div className="text-gray-900">Ordem não encontrada</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Ordem {ordem.numeroOP}</h1>
            <p className="text-gray-500">Detalhes da produção</p>
          </div>
        </div>
        <Badge variant={statusConfig[ordem.status].variant} className="text-base px-4 py-1">
          {statusConfig[ordem.status].label}
        </Badge>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Informações</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between">
              <span className="text-gray-500">Produto</span>
              <span className="text-gray-900 font-medium">{ordem.produto?.nome}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Máquina</span>
              <span className="text-gray-900 font-medium">{ordem.maquina?.nome}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Quantidade Solicitada</span>
              <span className="text-gray-900 font-medium">{ordem.quantidadeSolicitada}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Quantidade Boa</span>
              <span className="text-green-600 font-medium">{ordem.quantidadeBoa}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Quantidade Refugada</span>
              <span className="text-red-600 font-medium">{ordem.quantidadeRefugada}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Tempo Real</span>
              <span className="text-gray-900 font-mono">{formatDuration(ordem.tempoRealMinutos)}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Ações</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {ordem.status === 'NOVO' && (
              <Button
                variant="nuvem"
                className="w-full"
                leftIcon={<Play className="h-4 w-4" />}
                onClick={handleIniciar}
                isLoading={iniciarProducao.isPending}
              >
                Iniciar Produção
              </Button>
            )}

            {ordem.status === 'PRODUCAO' && (
              <>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Progresso (%)</label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={progresso}
                    onChange={(e) => setProgresso(Number(e.target.value))}
                    className="w-full"
                  />
                  <div className="text-center text-gray-500">{progresso}%</div>
                </div>
                <Button
                  variant="nuvem"
                  className="w-full"
                  leftIcon={<Camera className="h-4 w-4" />}
                >
                  Adicionar Foto
                </Button>
                <Button
                  variant="nuvem"
                  className="w-full"
                  leftIcon={<Square className="h-4 w-4" />}
                  onClick={handleFinalizar}
                  isLoading={finalizarProducao.isPending}
                >
                  Finalizar Produção
                </Button>
              </>
            )}

            {(ordem.status === 'AGUARDANDO_SEPARACAO' as any || ordem.status === 'PRONTO_EXPEDICAO' as any) && (
              <div className="text-center text-green-600">
                Produção finalizada em {ordem.dataFim ? formatDate(ordem.dataFim) : 'Data não informada'}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
